import React, {useContext, useEffect, useState} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {AuthContext} from "../../config/AuthContext";
import NavBar from "../components/NavBar";
import Login from "../root/Login";
import {Button, Card, Modal, Popconfirm, Radio, Select, Switch, Table, Tag, TimePicker, Tooltip} from "antd";
import Colors from "../../styles/Colors";
import {BiPlayCircle, BiRefresh, BiTime, BiEdit} from "react-icons/bi";
import {QuestionCircleOutlined} from "@ant-design/icons";
import Historials from "../components/Historials";
import {ColumnsType, TableProps} from "antd/es/table";
import toast, {Toaster} from "react-hot-toast";
import dayjs from "dayjs";
import {ExecutarJob} from "../../dao/automatitzar/ExecutarJob";
import {GetJobsConfig, JobConfig} from "../../dao/automatitzar/GetJobsConfig";
import {UpdateJobConfig} from "../../dao/automatitzar/UpdateJobConfig";

// Dies de la setmana per al cron de Spring (DOW: SUN..SAT o MON..)
const DIES = [
    {value: 'MON', label: 'Dilluns'},
    {value: 'TUE', label: 'Dimarts'},
    {value: 'WED', label: 'Dimecres'},
    {value: 'THU', label: 'Dijous'},
    {value: 'FRI', label: 'Divendres'},
    {value: 'SAT', label: 'Dissabte'},
    {value: 'SUN', label: 'Diumenge'},
];

// Descriu un cron de Spring (6 camps: seg min hora dia mes dow) de forma llegible
function descriuCron(cron: string): string {
    if (!cron) return '—';
    const parts = cron.trim().split(/\s+/);
    if (parts.length < 6) return cron;
    const [, min, hora, dia, , dow] = parts;
    const hhmm = `${String(hora).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    if (dow && dow !== '?' && dow !== '*') {
        const d = DIES.find(x => x.value === dow.toUpperCase());
        return `Setmanal · ${d ? d.label : dow} a les ${hhmm}`;
    }
    if (dia === '*' || dia === '?') return `Diari a les ${hhmm}`;
    return `Dia ${dia} a les ${hhmm}`;
}

const Jobs = () => {
    const titol: string = "Jobs";

    const {isLoggedIn, page} = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [jobs, setJobs] = useState<JobConfig[]>([]);

    // Modal d'edició de la programació
    const [editOpen, setEditOpen] = useState(false);
    const [jobEdit, setJobEdit] = useState<JobConfig | null>(null);
    const [freq, setFreq] = useState<'DIARI' | 'SETMANAL'>('DIARI');
    const [dia, setDia] = useState<string>('MON');
    const [hora, setHora] = useState(dayjs().hour(7).minute(0).second(0));
    const [actiu, setActiu] = useState<boolean>(true);

    useEffect(() => {
        if (isLoggedIn) carregar();
    }, [isLoggedIn]);

    async function carregar() {
        setLoading(true);
        try {
            setJobs(await GetJobsConfig(page));
        } finally {
            setLoading(false);
        }
    }

    async function executar(job: JobConfig) {
        setLoading(true);
        try {
            const res = await ExecutarJob(job.nom, page);
            if (res.ok) toast.success(res.message || 'Job executat correctament');
            else toast.error(res.message || 'Error executant el job');
            await carregar();
        } finally {
            setLoading(false);
        }
    }

    // Obre el modal parsejant el cron actual del job
    function obrirEdicio(job: JobConfig) {
        setJobEdit(job);
        setActiu(job.actiu === 1);
        const parts = (job.cron || '').trim().split(/\s+/);
        if (parts.length >= 6) {
            const min = parseInt(parts[1], 10) || 0;
            const h = parseInt(parts[2], 10) || 7;
            const dow = parts[5];
            setHora(dayjs().hour(h).minute(min).second(0));
            if (dow && dow !== '?' && dow !== '*') {
                setFreq('SETMANAL');
                setDia(dow.toUpperCase());
            } else {
                setFreq('DIARI');
            }
        } else {
            setFreq('DIARI');
            setHora(dayjs().hour(7).minute(0).second(0));
        }
        setEditOpen(true);
    }

    // Construeix el cron de Spring (6 camps) a partir del formulari
    function construirCron(): string {
        const min = hora.minute();
        const h = hora.hour();
        if (freq === 'SETMANAL') {
            return `0 ${min} ${h} ? * ${dia}`;
        }
        return `0 ${min} ${h} * * ?`;
    }

    async function desar() {
        if (!jobEdit) return;
        const cron = construirCron();
        const res = await UpdateJobConfig(jobEdit.nom, cron, actiu ? 1 : 0, page);
        if (res.ok) {
            toast.success('Programació actualitzada');
            setEditOpen(false);
            await carregar();
        } else {
            toast.error(res.message || 'Error actualitzant');
        }
    }

    async function toggleActiu(job: JobConfig, value: boolean) {
        const res = await UpdateJobConfig(job.nom, job.cron, value ? 1 : 0, page);
        if (res.ok) {
            toast.success(value ? 'Automatització activada' : 'Automatització desactivada');
            await carregar();
        } else {
            toast.error(res.message || 'Error');
        }
    }

    const columns: ColumnsType<JobConfig> = [
        {
            key: 'descripcio',
            title: 'Job',
            dataIndex: 'descripcio',
            render: (d, ele) => <b>{d || ele.nom}</b>,
        },
        {
            key: 'freq',
            title: 'Freqüència',
            dataIndex: 'cron',
            render: (cron) => <Tag color={Colors.colors.color4}><BiTime style={{marginRight: 4}}/>{descriuCron(cron)}</Tag>,
        },
        {
            key: 'proper',
            title: 'Proper llançament',
            dataIndex: 'properLlancament',
            render: (v, ele) => (
                ele.actiu === 1
                    ? (v ? <Tag color="blue">{v}</Tag> : <Tag>—</Tag>)
                    : <Tag color="default">Desactivat</Tag>
            ),
        },
        {
            key: 'ultima',
            title: 'Última execució',
            dataIndex: 'ultimaExecucio',
            responsive: ['lg'],
            render: (v) => v ? <Tag color="green">{v}</Tag> : <Tag>Mai</Tag>,
        },
        {
            key: 'actiu',
            title: 'Actiu',
            dataIndex: 'actiu',
            width: 90,
            render: (v, ele) => (
                <Switch
                    checked={v === 1}
                    onChange={(checked) => toggleActiu(ele, checked)}
                />
            ),
        },
        {
            key: 'accions',
            title: 'Accions',
            dataIndex: 'accions',
            width: 200,
            render: (_, job) => (
                <>
                    <Tooltip title="Modificar dia i freqüència">
                        <Button
                            icon={<BiEdit/>}
                            style={{backgroundColor: Colors.colors.color6, color: '#fff'}}
                            onClick={() => obrirEdicio(job)}
                        />
                    </Tooltip>
                    {" "}
                    <Popconfirm
                        title="Executar job"
                        description="Segur que vols executar aquest job ara (fora d'hora)?"
                        onConfirm={() => executar(job)}
                        icon={<QuestionCircleOutlined style={{color: 'orange'}}/>}
                    >
                        <Button
                            icon={<BiPlayCircle/>}
                            loading={loading}
                            style={{backgroundColor: Colors.colors.color2, color: '#fff'}}
                        >
                            Executar
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    const tableProps: TableProps<JobConfig> = {loading};

    if (!isLoggedIn) return <Login/>;

    return (
        <div>
            <Toaster/>
            <NavBar/>
            <div className={"general"}>
                <Card bordered={true} className={"elemento"}>
                    <div className={"actionButtons"}>
                        <Button title={"Refrescar"} style={{backgroundColor: Colors.colors.color4}}
                                onClick={carregar}><BiRefresh/></Button>
                    </div>
                    <h4 className={"titol"} style={{marginBottom: 16}}>{titol}</h4>
                    <Table
                        {...tableProps}
                        rowKey="nom"
                        columns={columns}
                        dataSource={jobs}
                        pagination={false}
                    />
                </Card>
                <br/>
                <Historials pantalla={"JOBS"} pagina={page}/>
            </div>

            <Modal
                title={jobEdit ? `Programació · ${jobEdit.descripcio || jobEdit.nom}` : 'Programació'}
                open={editOpen}
                onOk={desar}
                onCancel={() => setEditOpen(false)}
                okText="Desar"
                cancelText="Cancel·lar"
            >
                <div style={{display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <span style={{width: 110}}>Estat:</span>
                        <Switch checked={actiu} onChange={setActiu}
                                checkedChildren="Actiu" unCheckedChildren="Desactivat"/>
                    </div>

                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <span style={{width: 110}}>Freqüència:</span>
                        <Radio.Group value={freq} onChange={(e) => setFreq(e.target.value)}>
                            <Radio.Button value="DIARI">Diari</Radio.Button>
                            <Radio.Button value="SETMANAL">Setmanal</Radio.Button>
                        </Radio.Group>
                    </div>

                    {freq === 'SETMANAL' && (
                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                            <span style={{width: 110}}>Dia:</span>
                            <Select
                                value={dia}
                                onChange={setDia}
                                style={{width: 200}}
                                options={DIES}
                            />
                        </div>
                    )}

                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <span style={{width: 110}}>Hora:</span>
                        <TimePicker
                            value={hora}
                            onChange={(v) => v && setHora(v)}
                            format="HH:mm"
                            allowClear={false}
                        />
                    </div>

                    <div style={{fontSize: 12, color: '#888'}}>
                        Resum: {freq === 'SETMANAL'
                            ? `cada ${DIES.find(d => d.value === dia)?.label} a les ${hora.format('HH:mm')}`
                            : `cada dia a les ${hora.format('HH:mm')}`}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Jobs;
